import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn, getNavIcon, useTheme } from '@v2/design-system';
import { siteConfig } from '@v2/config/site';
import { useNavigationMeta } from '@v2/hooks/useResourceCrud';
import { useI18n } from '@v2/providers/I18nProvider';

/** Marca ativo só o item mais específico (evita /estoque + /estoque/aprovacoes). */
function isNavHrefActive(pathname: string, href: string, allHrefs: string[]): boolean {
    const path = pathname === '' ? '/' : pathname;
    const target = href === '/' ? '/' : href;

    if (target === '/') {
        return path === '/';
    }

    const matches = path === target || path.startsWith(`${target}/`);
    if (!matches) {
        return false;
    }

    return !allHrefs.some((other) => {
        if (other === target || other === '/') {
            return false;
        }

        return other.length > target.length
            && other.startsWith(`${target}/`)
            && (path === other || path.startsWith(`${other}/`));
    });
}

export default function Sidebar({ className }: { className?: string }) {
    const { t } = useI18n();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const pathname = useLocation().pathname;
    const { data: navigation } = useNavigationMeta();

    const logo = isDark ? siteConfig.logoDark : siteConfig.logo;

    const allHrefs = useMemo(
        () => (navigation ?? []).flatMap((group) => group.items.map((item) => item.href)),
        [navigation],
    );

    const resolveActiveGroup = (nav: typeof navigation, path: string, hrefs: string[]) => {
        for (const group of nav ?? []) {
            for (const item of group.items) {
                if (isNavHrefActive(path, item.href, hrefs)) {
                    return group.group;
                }
            }
        }
        return (nav ?? [])[0]?.group ?? null;
    };

    const [openGroup, setOpenGroup] = useState<string | null>(null);

    useEffect(() => {
        if (navigation) {
            setOpenGroup((prev) => prev ?? resolveActiveGroup(navigation, pathname, allHrefs));
        }
    }, [navigation, pathname, allHrefs]);

    const toggleGroup = (groupKey: string) => {
        setOpenGroup((prev) => (prev === groupKey ? null : groupKey));
    };

    return (
        <aside
            className={cn(
                'fixed bottom-0 start-0 z-50 h-full w-[270px] overflow-y-auto border-e 2xl:w-72',
                'border-gray-200 bg-white dark:border-gray-700/60 dark:bg-gray-900',
                className,
            )}
        >
            <div className={cn(
                'sticky top-0 z-40 px-6 pb-5 pt-5 2xl:px-8 2xl:pt-6',
                'bg-white dark:bg-gray-900',
            )}>
                <Link to="/" className="inline-flex items-center gap-2">
                    <img src={logo} alt={siteConfig.name} className="h-8" />
                </Link>
            </div>

            <nav className="space-y-1 px-3 pb-24 2xl:px-5">
                {(navigation ?? []).map((group) => {
                    const isOpen = openGroup === group.group;
                    const hasActiveItem = group.items.some((item) =>
                        isNavHrefActive(pathname, item.href, allHrefs),
                    );

                    return (
                        <div key={group.group}>
                            <button
                                type="button"
                                onClick={() => toggleGroup(group.group)}
                                className={cn(
                                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors',
                                    hasActiveItem
                                        ? 'text-primary-dark dark:text-amber-400'
                                        : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300',
                                )}
                            >
                                {group.label}
                                <svg
                                    className={cn('h-3.5 w-3.5 shrink-0 transition-transform duration-200', isOpen && 'rotate-180')}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {isOpen && (
                                <ul className="mt-1 mb-2 space-y-1">
                                    {group.items.map((item) => {
                                        const href = item.href === '/' ? '/' : item.href;
                                        const isActive = isNavHrefActive(pathname, item.href, allHrefs);
                                        const Icon = getNavIcon(href);

                                        return (
                                            <li key={item.href}>
                                                <Link
                                                    to={href}
                                                    className={cn(
                                                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                                                        isActive
                                                            ? 'bg-primary-lighter text-primary-dark dark:bg-amber-900/30 dark:text-amber-400'
                                                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
                                                    )}
                                                >
                                                    <Icon className="h-[22px] w-[22px] shrink-0 opacity-80" />
                                                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                                                    {'badge' in item && typeof item.badge === 'number' && item.badge > 0 && (
                                                        <span
                                                            className={cn(
                                                                'ms-auto inline-flex shrink-0 animate-pulse items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                                                'badge_tone' in item && item.badge_tone === 'warning'
                                                                    ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-300'
                                                                    : 'border-red-500 bg-red-50 text-red-600 dark:border-red-400 dark:bg-red-950/40 dark:text-red-300',
                                                            )}
                                                        >
                                                            {item.badge > 99 ? '99+' : item.badge}
                                                        </span>
                                                    )}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </nav>

        </aside>
    );
}
